import { Reveal } from "@/components/Motion";

const MONTHS = ["MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV"];
const FIRST = 3; // March

const spanStyle = (s, e) => ({
    left: `${((s - FIRST) / MONTHS.length) * 100}%`,
    width: `${((e - s + 1) / MONTHS.length) * 100}%`,
});

const peakSegments = (peak) => {
    const sorted = [...peak].sort((a, b) => a - b);
    const segs = [];
    let start = null;
    let prev = null;
    for (const m of sorted) {
        if (start === null) {
            start = m;
            prev = m;
        } else if (m === prev + 1) {
            prev = m;
        } else {
            segs.push([start, prev]);
            start = m;
            prev = m;
        }
    }
    if (start !== null) segs.push([start, prev]);
    return segs;
};

export default function HatchCalendar({ rows, pin }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-[#0D1626] p-5 sm:p-8" data-testid="hatch-calendar">
            <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3 md:gap-4 px-4 pb-3 border-b border-white/10">
                <span className="hidden md:block self-end font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Hatch</span>
                <div className="grid grid-cols-9 font-mono text-[8px] md:text-[9px] tracking-wider uppercase text-slate-500">
                    {MONTHS.map((m) => (
                        <span key={m} className="text-center">{m}</span>
                    ))}
                </div>
            </div>

            <div className="mt-4 md:mt-0 md:divide-y md:divide-white/5 space-y-4 md:space-y-0">
                {rows.map((row, i) => (
                    <Reveal key={row.name} delay={i * 0.05}>
                        <div
                            data-testid={`hatch-cal-row-${row.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="group relative grid md:grid-cols-[200px_1fr] gap-3 md:gap-4 rounded-xl md:rounded-none bg-[#111C2E] md:bg-transparent p-4 md:py-4 md:pr-12 border border-white/10 md:border-0 hover:bg-[#1A273D] transition-colors duration-300"
                        >
                            <div className="flex items-start gap-3 pr-9 md:pr-0">
                                <span className="mt-1.5 h-8 w-1.5 rounded-full shrink-0" style={{ background: row.color }} />
                                <div>
                                    <span className="block font-serif text-lg font-semibold text-slate-50 leading-tight">{row.name}</span>
                                    <span className="mt-1 block font-mono text-[10px] tracking-wider text-slate-500">
                                        {row.size} · {row.patterns}
                                    </span>
                                </div>
                            </div>

                            <div className="relative self-center h-9 rounded-lg bg-white/[0.03] overflow-hidden">
                                <div className="absolute inset-0 grid grid-cols-9">
                                    {MONTHS.map((m) => (
                                        <span key={m} className="border-l border-white/5 first:border-l-0" />
                                    ))}
                                </div>
                                {row.spans.map((span, j) => (
                                    <div key={j}>
                                        <div
                                            className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded-full opacity-25"
                                            style={{ ...spanStyle(span.s, span.e), background: row.color }}
                                        />
                                        {peakSegments(span.peak).map(([ps, pe], k) => (
                                            <div
                                                key={k}
                                                className="absolute top-1/2 -translate-y-1/2 h-2.5 rounded-full"
                                                style={{ ...spanStyle(ps, pe), background: row.color, boxShadow: `0 0 10px ${row.color}66` }}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>

                            {pin && (
                                <a
                                    href={`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(pin.url)}&media=${encodeURIComponent(pin.media)}&description=${encodeURIComponent(`${row.name}: ${row.patterns}, hook sizes ${row.size}. ${pin.title}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`pin-cal-${row.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    aria-label={`Pin ${row.name} hatch to Pinterest`}
                                    title={`Pin ${row.name}`}
                                    className="absolute top-3 right-3 md:top-1/2 md:-translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0B1320]/80 backdrop-blur-md border border-white/15 text-slate-300 md:opacity-0 md:group-hover:opacity-100 hover:bg-[#E60023] hover:border-[#E60023] hover:text-white transition-all duration-300"
                                >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.739a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </Reveal>
                ))}
            </div>

            <p className="mt-6 px-4 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-600">
                Full bar = active months · Bright core = peak weeks
            </p>
            <p className="mt-2 px-4 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-600">
                Timing shifts 2–4 weeks earlier on Pacific & southern streams, later in the Rockies & northern Canada
            </p>
        </div>
    );
}
